
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, User as UserIcon, Trophy, CreditCard, CheckCircle } from "lucide-react";
import { uploadAvatar } from '@/integrations/supabase/setupStorage';
import { supabase } from '@/integrations/supabase/client';
import { triggerProfileUpdate } from '@/utils/onboardingEvents';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import UserObjectives from '@/components/UserObjectives';
import UserAchievements from '@/components/UserAchievements';
import { useRecipes } from '@/contexts/RecipesContext';
import { Recipe } from '@/types';
import { Bookmark } from 'lucide-react';
import { Spinner } from '@/components/Spinner';
import { createStripePortalSession } from '@/utils/stripeUtils';

const objectives = [
  {
    id: 'healthy_eating',
    name: 'Comer saludable',
  },
  {
    id: 'plant_based',
    name: 'Transición plant based',
  },
  {
    id: 'weight_loss',
    name: 'Perder grasa',
  },
  {
    id: 'muscle_gain',
    name: 'Aumentar masa muscular',
  },
  {
    id: 'revert_pathology',
    name: 'Revertir patología',
  },
  {
    id: 'improve_digestion',
    name: 'Mejorar digestiones',
  },
  {
    id: 'body_recomposition',
    name: 'Recomposición corporal',
  },
  {
    id: 'varied_food',
    name: 'Comer más variado',
  },
  {
    id: 'learn_recipes',
    name: 'Aprender recetas',
  },
  {
    id: 'meet_people',
    name: 'Conocer a nuevas personas',
  },
];

const Profile = () => {
  const { user, updateUser, updateProfileImage, hasActiveSubscription } = useAuth();
  const { recipes } = useRecipes();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [description, setDescription] = useState(user?.description || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatarUrl || null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>(user?.objectives || []);
  const [favoriteRecipes, setFavoriteRecipes] = useState<Recipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const navigate = useNavigate();
  const initialLoadRef = useRef(true);
  const hasUserDataChangedRef = useRef(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);

  useEffect(() => {
    if (user && initialLoadRef.current) {
      setName(user.name || '');
      setEmail(user.email || '');
      setAvatarPreview(user.avatarUrl || null);
      setDescription(user.description || '');
      setSelectedObjectives(user.objectives || []);
      
      loadFavoriteRecipes();
      
      if (user.id && !user.id.startsWith('user-')) {
        fetchUserProfile(user.id);
      }
      
      initialLoadRef.current = false;
    }
  }, [user]);
  
  useEffect(() => {
    if (!initialLoadRef.current && user) {
      loadFavoriteRecipes();
    }
  }, [recipes, user?.favorites]);
  
  const loadFavoriteRecipes = () => {
    if (!user || !user.favorites || !recipes.length) {
      return;
    }
    
    setLoadingRecipes(true);
    
    try {
      const userFavorites = user.favorites || [];
      const userFavoriteRecipes = recipes.filter(recipe => userFavorites.includes(recipe.id));
      setFavoriteRecipes(userFavoriteRecipes);
    } catch (error) {
      console.error('Error loading favorite recipes:', error);
    } finally {
      setLoadingRecipes(false);
    }
  };
  
  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('Fetching user profile data for ID:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('description, objectives, favorites')
        .eq('id', userId)
        .single();
        
      if (error) {
        console.error('Error fetching profile:', error);
      } else if (data) {
        console.log('Profile data retrieved:', data);
        setDescription(data.description || '');
        setSelectedObjectives(data.objectives || []);
        
        if ((data.description || '') !== (user.description || '') || 
            !arraysEqual(data.objectives || [], user.objectives || []) ||
            !arraysEqual(data.favorites || [], user.favorites || [])) {
          
          updateUser({
            description: data.description || '',
            objectives: data.objectives || [],
            favorites: data.favorites || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const arraysEqual = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasUserDataChangedRef.current) {
      toast.info('No hay cambios para guardar');
      return;
    }
    
    setIsUpdating(true);
    
    try {
      if (!user || !user.id) {
        throw new Error('No user logged in');
      }
      
      console.log('Updating profile with data:', {
        name,
        description,
        objectives: selectedObjectives
      });
      
      await updateUser({ 
        name, 
        email,
        description,
        objectives: selectedObjectives
      });
      
      await fetchUserProfile(user.id);
      
      window.dispatchEvent(new CustomEvent('profile-updated', { 
        detail: { 
          userId: user.id,
          weight: user.profile?.weight || 0
        } 
      }));
      
      triggerProfileUpdate();
      
      hasUserDataChangedRef.current = false;
      
      toast.success('Perfil actualizado correctamente');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (value !== user?.name) {
      hasUserDataChangedRef.current = true;
    }
  };
  
  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    if (value !== user?.description) {
      hasUserDataChangedRef.current = true;
    }
  };
  
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value !== user?.email) {
      hasUserDataChangedRef.current = true;
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setIsChangingPassword(true);
    setPasswordChangeSuccess(false);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('Error updating password:', error);
        toast.error(`Error al cambiar la contraseña: ${error.message}`);
        return;
      }

      toast.success('Contraseña cambiada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordChangeSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setPasswordChangeSuccess(false);
      }, 5000);
      
    } catch (error: any) {
      console.error('Unexpected error changing password:', error);
      toast.error('Error inesperado al cambiar la contraseña');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.match('image.*')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor de 5MB');
      return;
    }
    
    setIsUpdating(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        setAvatarPreview(result);
        
        if (user && user.id) {
          try {
            const uploadResult = await uploadAvatar(file, user.id);
            
            if (uploadResult && 'error' in uploadResult) {
              console.error('Error uploading avatar:', uploadResult.error);
              toast.error('Error al subir la imagen. Se utilizará almacenamiento local.');
            }
            
            const imageUrl = uploadResult && 'url' in uploadResult ? uploadResult.url : result;
            updateProfileImage(imageUrl);
            
            toast.success('Imagen de perfil actualizada correctamente');
          } catch (error) {
            console.error('Error processing avatar:', error);
            toast.error('Error al procesar la imagen');
          }
        } else {
          updateProfileImage(result);
          toast.success('Imagen de perfil actualizada correctamente (modo local)');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error changing avatar:', error);
      toast.error('Error al cambiar la imagen de perfil');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'No disponible';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'No disponible';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map((part) => part[0]).join('').toUpperCase();
  };

  const toggleObjective = (objectiveId: string) => {
    setSelectedObjectives(prev => {
      const newObjectives = prev.includes(objectiveId)
        ? prev.filter(id => id !== objectiveId)
        : [...prev, objectiveId];
      
      hasUserDataChangedRef.current = !arraysEqual(newObjectives, user?.objectives || []);
      
      return newObjectives;
    });
  };

  const getSubscriptionDisplayInfo = () => {
    if (!user?.subscription) {
      return { status: 'Inactiva', color: 'text-yellow-600' };
    }
    
    // Check if user is in trial period
    if (user.subscription.isTrialing && user.subscription.trialEnd) {
      const trialEndDate = new Date(user.subscription.trialEnd);
      const now = new Date();
      if (trialEndDate > now) {
        return { status: 'En Prueba', color: 'text-blue-600' };
      }
    }
    
    // Check regular subscription status
    if (user.subscription.status === 'active') {
      return { status: 'Activa', color: 'text-green-600' };
    }
    
    return { status: 'Inactiva', color: 'text-yellow-600' };
  };

  const hasActiveSubscriptionOrTrial = () => {
    return hasActiveSubscription();
  };

  const handleManageSubscription = async () => {
    if (!user || !user.email) {
      toast.error('Por favor, inicia sesión para gestionar tu suscripción.');
      navigate('/login');
      return;
    }
    
    setIsManagingSubscription(true);
    try {
      // Explicitly request cancellation enabled portal
      const portalUrl = await createStripePortalSession(user.email);
      if (!portalUrl) {
        throw new Error("No se pudo obtener la URL del portal");
      }
      
      console.log("[Profile] Redirecting to Stripe portal URL");
      toast.info('Accediendo al portal de gestión de suscripción...');
      
      // Ensure we're using a direct, full-page navigation to avoid Stripe portal issues
      window.location.href = portalUrl;
      
    } catch (error) {
      console.error("[Profile] Error creating portal session:", error);
      toast.error('No se pudo acceder al portal de gestión. Por favor, inténtalo de nuevo más tarde.');
      setIsManagingSubscription(false);
    }
  };

  return (
    <ProtectedRoute requireSubscription={false}>
      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow bg-gray-50">
          <div className="container mx-auto">
            <h1 className="text-3xl font-bold text-vegana-primary px-4 py-6">Mi Perfil</h1>
            
            <Tabs defaultValue="general">
              <TabsList className="mb-8 w-full border-b">
                <TabsTrigger value="general">Información General</TabsTrigger>
                <TabsTrigger value="recipes">Recetas Guardadas</TabsTrigger>
                <TabsTrigger value="password">Contraseña</TabsTrigger>
                <TabsTrigger value="subscription">Suscripción</TabsTrigger>
              </TabsList>
              
              <TabsContent value="general">
                <Card>
                  <CardHeader>
                    <CardTitle>Información Personal</CardTitle>
                    <CardDescription>
                      Actualiza tu información de perfil
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center mb-8">
                      <div className="relative group">
                        <Avatar className="h-24 w-24 border-2 border-white shadow-lg">
                          <AvatarImage src={avatarPreview || undefined} alt={name} className="object-cover" />
                          <AvatarFallback className="bg-vegana-primary text-white text-xl">
                            {getInitials(name)}
                          </AvatarFallback>
                        </Avatar>
                        <label 
                          htmlFor="avatar-upload" 
                          className="absolute bottom-0 right-0 p-1 bg-vegana-primary rounded-full text-white cursor-pointer hover:bg-vegana-secondary transition-colors"
                        >
                          <Camera size={16} />
                        </label>
                        <input 
                          type="file" 
                          id="avatar-upload" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleAvatarChange}
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                    
                    <form onSubmit={handleUpdateProfile} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nombre</Label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          disabled={isUpdating}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Descripción</Label>
                        <Textarea
                          id="description"
                          value={description}
                          onChange={(e) => handleDescriptionChange(e.target.value)}
                          disabled={isUpdating}
                          placeholder="Cuéntanos un poco sobre ti..."
                          className="resize-none"
                          rows={4}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          disabled={isUpdating}
                        />
                      </div>
                      
                      <div className="space-y-3 pt-4 border-t">
                        <Label>Mis objetivos</Label>
                        <p className="text-sm text-gray-500">
                          Selecciona los objetivos que mejor describan tu propósito en la plataforma
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                          {objectives.map((objective) => (
                            <div key={objective.id} className="flex items-center space-x-2">
                              <Checkbox 
                                id={objective.id} 
                                checked={selectedObjectives.includes(objective.id)}
                                onCheckedChange={() => toggleObjective(objective.id)}
                              />
                              <label
                                htmlFor={objective.id}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                {objective.name}
                              </label>
                            </div>
                          ))}
                        </div>
                        
                        {selectedObjectives.length > 0 && (
                          <div className="pt-4">
                            <p className="text-sm font-medium mb-2">Vista previa:</p>
                            <UserObjectives objectives={selectedObjectives} />
                          </div>
                        )}
                      </div>
                      
                      {user?.id && (
                        <div className="space-y-3 pt-4 border-t">
                          <Label className="flex items-center gap-2">
                            <Trophy className="h-5 w-5 text-amber-500" />
                            Mis Logros
                          </Label>
                          <p className="text-sm text-gray-500">
                            Insignias y reconocimientos que has obtenido
                          </p>
                          <UserAchievements userId={user.id} />
                        </div>
                      )}
                      
                      <Button 
                        type="submit" 
                        className="bg-vegana-primary hover:bg-vegana-primary/90"
                        disabled={isUpdating || !hasUserDataChangedRef.current}
                      >
                        {isUpdating ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="recipes">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bookmark className="h-5 w-5" />
                      Mis Recetas Guardadas
                    </CardTitle>
                    <CardDescription>
                      Recetas que has guardado como favoritas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingRecipes ? (
                      <div className="flex justify-center py-8">
                        <Spinner size="md" />
                      </div>
                    ) : favoriteRecipes.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {favoriteRecipes.map(recipe => (
                          <div 
                            key={recipe.id} 
                            className="cursor-pointer group"
                            onClick={() => navigate(`/recetas/${recipe.id}`)}
                          >
                            <div className="relative aspect-square rounded-md overflow-hidden mb-2">
                              <img 
                                src={recipe.imageUrl} 
                                alt={recipe.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            <h3 className="text-sm font-medium line-clamp-2 group-hover:text-vegana-primary transition-colors">
                              {recipe.title}
                            </h3>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Bookmark className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No has guardado ninguna receta como favorita.</p>
                        <Button 
                          variant="link" 
                          onClick={() => navigate('/recetas')}
                          className="mt-2"
                        >
                          Explorar recetas
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="password">
                <Card>
                  <CardHeader>
                    <CardTitle>Cambiar Contraseña</CardTitle>
                    <CardDescription>
                      Actualiza tu contraseña para mantener tu cuenta segura
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {passwordChangeSuccess && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Contraseña cambiada con éxito</span>
                      </div>
                    )}
                    <form onSubmit={handleChangePassword} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">Nueva Contraseña</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={isChangingPassword}
                          placeholder="Mínimo 8 caracteres"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirmar Nueva Contraseña</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={isChangingPassword}
                          placeholder="Repite la nueva contraseña"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="bg-vegana-primary hover:bg-vegana-primary/90"
                        disabled={isChangingPassword || !newPassword || !confirmPassword}
                      >
                        {isChangingPassword ? (
                          <>
                            <Spinner size="sm" />
                            <span className="ml-2">Cambiando contraseña...</span>
                          </>
                        ) : (
                          'Cambiar Contraseña'
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="subscription">
                <Card>
                  <CardHeader>
                    <CardTitle>Tu Suscripción</CardTitle>
                    <CardDescription>
                      Gestiona los detalles de tu suscripción
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="p-4 rounded-lg bg-gray-100">
                      <p className="font-semibold">Estado: {' '}
                        <span className={getSubscriptionDisplayInfo().color}>
                          {getSubscriptionDisplayInfo().status}
                        </span>
                      </p>
                      {user?.subscription.endDate && (
                        <p className="text-gray-600 mt-2">
                          {user.subscription.isTrialing && user.subscription.trialEnd && new Date(user.subscription.trialEnd) > new Date() 
                            ? `Fin de prueba: ${formatDate(user.subscription.trialEnd)}`
                            : `Próxima renovación: ${formatDate(user.subscription.endDate)}`
                          }
                        </p>
                      )}
                    </div>
                    
                    {hasActiveSubscriptionOrTrial() ? (
                      <Button 
                        className="w-full flex items-center justify-center gap-2"
                        onClick={handleManageSubscription}
                        disabled={isManagingSubscription}
                      >
                        {isManagingSubscription ? (
                          <>
                            <Spinner size="sm" />
                            <span>Redirigiendo al portal...</span>
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4" />
                            <span>Gestionar Suscripción</span>
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        className="w-full bg-vegana-primary hover:bg-vegana-primary/90"
                        onClick={() => navigate('/suscripcion')}
                      >
                        Activar Suscripción
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>
        
        <Footer />
      </div>
    </ProtectedRoute>
  );
};

export default Profile;
